import * as Dialog from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import type { ReactNode } from "react"
import { useTranslation } from "@/hooks/use-translation"
import { cn } from "@/lib/utils"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  description?: ReactNode
  children: ReactNode
  footer?: ReactNode
  contentClassName?: string
  bodyClassName?: string
  headerActions?: ReactNode
  /** Raise above fullscreen hosts like ShotPanel (z 100/101). */
  nested?: boolean
}

export function ScaleSheetDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  contentClassName,
  bodyClassName,
  headerActions,
  nested = false,
}: Props) {
  const { t } = useTranslation(["workspace", "common"])
  const overlayZ = nested ? "z-[110]" : "z-[100]"
  const contentZ = nested ? "z-[111]" : "z-[101]"

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn("ui-dialog-overlay fixed inset-0 bg-[var(--ui-overlay-heavy)] backdrop-blur-sm", overlayZ)}
        />
        <Dialog.Content
          className={cn(
            "ui-dialog-content fixed top-1/2 left-1/2 flex max-h-[92vh] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden",
            contentZ,
            "rounded-lg border border-ui-border shadow-2xl shadow-black/50 focus:outline-none dark:bg-zinc-950 light:bg-ui-bg-elevated",
            contentClassName ?? "w-[min(96vw,920px)]",
          )}
        >
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-ui-border p-5 pb-4">
            <div className="min-w-0">
              <Dialog.Title className="text-lg font-semibold text-ui-text-primary">{title}</Dialog.Title>
              {description ? (
                <Dialog.Description className="mt-1 text-xs text-ui-text-secondary">{description}</Dialog.Description>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              {headerActions}
              <Dialog.Close
                type="button"
                className="shrink-0 rounded-lg p-1.5 text-ui-text-muted hover:bg-ui-surface-raised hover:text-ui-text-primary"
                aria-label={t("close", { ns: "common" })}
              >
                <X className="h-5 w-5" />
              </Dialog.Close>
            </div>
          </div>

          <div className={cn("min-h-0 flex-1 space-y-4 overflow-y-auto p-5 pt-4", bodyClassName)}>{children}</div>

          {footer ? <div className="shrink-0 border-t border-ui-border px-5 py-4">{footer}</div> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
